import React, { useRef, useMemo } from 'react'
import classNames from 'classnames'
import CustomButton from '../../../features/customButton/ui/main/CustomButton'
import { useUser } from '../../../../redux/reducer/user'
import { useModal } from '../../../../hooks/useModal'
import { soundManager } from '../../../../utils/scene/sound/SoundManager'
import { customizationBlockItemShowAnimation } from './customizationBlockItemShowAnimation'
import { customizationContent } from '../../../../constants/copyright'
import { useTranslation } from '../../../../translations'
import { useFeatureToggle } from '../../../../hooks/useFeatureToggle'
import Icon from '../../../baseComponents/gui/icon/Icon'

const CustomizationBlockItem = React.forwardRef(function CustomizationBlockItem(
  {
    isHidden,
    item,
    index,
    id,
    cost,
    slot,
    preview_image,
    callbacks,
    customState,
    blocked_image,
    isNew = false,
    product = {},
    filter,
    beautiesCost,
    giftCost,
    isBeauties = false,
  },
  ref,
) {
  const itemRef = useRef(null)
  const buttonRef = useRef(null)
  const imageRef = useRef(null)
  const { t } = useTranslation()

  const { addModal } = useModal()
  const { profile: { items, money } = {} } = useUser()
  const userItem = items.find(({ id: itemId }) => itemId === id)

  const isRoomState = customState === 'room'
  const isCharacterState = customState === 'character'
  const isBeautiesState = customState === 'beauties'

  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  const currentCost = useMemo(() => {
    if (isBeautiesState) {
      return gameTamagotchiBeauties ? beautiesCost : giftCost
    }

    return cost
  }, [beautiesCost, cost, giftCost, isBeautiesState])

  const getCorrectSound = () => {
    if (
      isRoomState ||
      (isBeautiesState && filter === 'room') ||
      (isBeautiesState && filter === 'shower')
    ) {
      return 'applyRoom'
    } else if (isCharacterState || (isBeautiesState && filter === 'character')) {
      return 'applyCloth'
    }
  }

  const customizationItemAction = () => {
    if (userItem?.is_equipped) return

    const sound = getCorrectSound()

    if (userItem && !userItem.is_equipped) {
      soundManager.onPlay(sound)
      callbacks.equipItem(id)
      return
    }

    if (cost === 0 && !isBeautiesState) {
      soundManager.onPlay(sound)
      callbacks.buyItem(id)
      return
    }

    const isCanBuy = item?.cost <= money ? 'can' : 'cant'

    // Если таб бьютисов, то по клику открываем модалку buyBeautiesModal
    if (isBeautiesState) {
      addModal({
        type: 'buyBeautiesModal',
        props: { itemId: id, productId: product.id, callbacks, showXp: false, filter },
      })
    } else {
      addModal({
        type: 'buyModal',
        props: {
          itemData: {
            can: { xp: item?.reward_score, priceData: { moneyCost: item?.cost } },
            cant: {},
          }[isCanBuy],
          title: {
            can: t('customization.buyTitle'),
            cant: t('customization.buyCantTitle'),
          }[isCanBuy],
          subTitle: { cant: t('customization.buySubtitle') }[isCanBuy],
          callbacks: { buy: () => callbacks.buyItem(item?.id) },
          buttons: customizationContent[{ can: 'buyButtons', cant: 'cantBuyButtons' }[isCanBuy]],
        },
      })
    }
  }

  // customizationBlockItemShowAnimation({ index, itemRef, buttonRef, imageRef });

  const buttonText = useMemo(() => {
    // если товар за бьютисы или гифты
    if (isBeautiesState) {
      if (!userItem && currentCost > 0) {
        // Предмет не куплен
        return t('customization.more')
      } else {
        if (userItem?.is_equipped) {
          // Предмет куплен и надет
          return ''
        } else {
          // Предмет куплен и его можно надеть или применить
          // Надеваем на персонажа, применяем (ставим) предмет в комнату
          if (!userItem?.image && !item?.compresed_images && currentCost > 0) {
            return t('customization.unequip')
          } else {
            return isBeautiesState && filter === 'character'
              ? t('customization.equip')
              : t('customization.apply')
          }
        }
      }
    } else {
      // если товар за коины
      if (!userItem && cost > 0) {
        // Предмет не куплен или цена 0
        return cost
      } else {
        if (userItem?.is_equipped) {
          // Предмет куплен и надет
          return ''
        } else {
          // Предмет куплен и его можно надеть или применить
          if (!item?.image && !item?.compresed_images) {
            return t('customization.unequip')
          } else {
            return isRoomState ? t('customization.apply') : t('customization.equip')
          }
        }
      }
    }
  }, [isBeautiesState, currentCost, userItem])

  const buttonClasses = useMemo(() => {
    return {
      'button_product-buy': !userItem && currentCost > 0,
      'button_product-unequip': userItem?.is_equipped || (!userItem && currentCost === 0),
      'button_product-equip': userItem?.is_equipped === false,
    }
  }, [userItem, currentCost])

  const iconClass = useMemo(() => {
    // Для товаров НЕ за бьютисы и НЕ купленных выводим иконку coin-black
    if (!isBeautiesState && !userItem && cost > 0) {
      return 'coin-black'
    }

    // Для товаров за бьютисы и НЕ купленных НЕ выводим иконку
    if (isBeautiesState && !userItem) {
      return ''
    }

    // Товар куплен и применен (надет) выводим иконку equipped
    if (userItem?.is_equipped) {
      return 'equipped'
    }

    // В остальных случаях не выводим иконку
    return ''
  }, [userItem])

  return (
    <div
      className={classNames('customization__item', { customization__item_inactive: isHidden })}
      ref={itemRef}
      onClick={customizationItemAction}
    >
      <div
        className={classNames('customization__item-image', {
          'customization__item-image_color': ['color'].includes(slot),
        })}
        ref={imageRef}
      >
        <img
          src={preview_image}
          alt={'preview'}
        />
      </div>

      {!isHidden && (
        <div
          className={'customization__item-button-wrapper'}
          ref={buttonRef}
        >
          <CustomButton
            text={buttonText}
            icon={iconClass}
            className={classNames('customization__item-button button_product', buttonClasses)}
          />
        </div>
      )}

      {isNew && <span className={'customization__item-new'}>{'new'}</span>}

      {isBeauties && !isNew && (
        <Icon
          name={'beauties'}
          className={'customization__item-beauties'}
        />
      )}
    </div>
  )
})

export default CustomizationBlockItem
CustomizationBlockItem.propTypes = {}
